import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../../config/api";
import Sidebar from "../../../../../../components/Sidebar";
import LanguageSwitcher from "../../../../../../components/LanguageSwitcher";
import { ListType } from "../../../../Product/tabs/upload/addProduct/attributeType/listType";
import { StringType } from "../../../../Product/tabs/upload/addProduct/attributeType/stringType";
import { TableType } from "../../../../Product/tabs/upload/addProduct/attributeType/tableType";
import { ProgressSteps } from "../../../../Product/tabs/upload/addProduct/ProgressSteps";
import PdfCropModal from "../../../../../../components/PdfCropModal";
import ConflictModal from "./ConflictModal";

// ── Term Changes Table ────────────────────────────────────────────────────────
function TermChangesPanel({ termChanges, language }) {
    const [open, setOpen] = useState(true);

    // Normalise all possible shapes the backend may send:
    // 1. { term_changes: { attribute_name: [...], ... } }  (double-wrapped)
    // 2. { attribute_name: [...], ... }                    (flat – most common)
    // 3. undefined / null / non-object                     (no data)
    let changes = {};
    if (
        termChanges &&
        typeof termChanges === "object" &&
        !Array.isArray(termChanges)
    ) {
        const unwrapped = termChanges.term_changes;
        if (
            unwrapped &&
            typeof unwrapped === "object" &&
            !Array.isArray(unwrapped)
        ) {
            changes = unwrapped;
        } else {
            changes = termChanges;
        }
    }

    const totalChanges = Object.values(changes).reduce(
        (s, arr) => s + (Array.isArray(arr) ? arr.length : 0),
        0,
    );

    if (totalChanges === 0) {
        // Nothing to show — hide the panel entirely
        return null;
    }

    const typeLabel = {
        attribute_name: "Attribute Name",
        unit: "Unit",
        category_value: "Category Value",
    };

    return (
        <div className="bg-white rounded-2xl border border-orange-200 overflow-hidden">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full min-h-[56px] flex items-center justify-between px-6 py-4 text-left hover:bg-orange-50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <svg
                            className="w-4 h-4 text-orange-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                            />
                        </svg>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-800 text-sm">
                            {language === "zh"
                                ? "AI 術語正規化"
                                : "AI Term Normalisation"}
                        </p>
                        <p className="text-xs text-gray-500">
                            {language === "zh"
                                ? `${totalChanges} 個術語已被標準化`
                                : `${totalChanges} terms were normalised`}
                        </p>
                    </div>
                </div>
                <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {open && (
                <div className="px-6 pb-5 space-y-4 border-t border-orange-100">
                    {Object.entries(changes).map(([type, rows]) =>
                        !Array.isArray(rows) || rows.length === 0 ? null : (
                            <div key={type}>
                                <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mt-4 mb-2">
                                    {typeLabel[type] || type}
                                </p>
                                <div className="overflow-x-auto rounded-lg border border-gray-200">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-gray-50 text-gray-500">
                                                <th className="px-4 py-2 text-left font-medium">
                                                    {language === "zh"
                                                        ? "原始"
                                                        : "Raw"}
                                                </th>
                                                <th className="px-4 py-2 text-center text-gray-300">
                                                    →
                                                </th>
                                                <th className="px-4 py-2 text-left font-medium">
                                                    {language === "zh"
                                                        ? "標準化"
                                                        : "Canonical"}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r, i) => (
                                                <tr
                                                    key={i}
                                                    className="border-t border-gray-100"
                                                >
                                                    <td className="px-4 py-2 text-red-500 line-through font-mono">
                                                        {r.raw}
                                                    </td>
                                                    <td className="px-4 py-2 text-center text-gray-300">
                                                        →
                                                    </td>
                                                    <td className="px-4 py-2 text-green-600 font-mono">
                                                        {r.canonical}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ),
                    )}
                </div>
            )}
        </div>
    );
}

// ── Image Gallery ─────────────────────────────────────────────────────────────
function ImageGallery({ images, language, onDelete, onAdd, fileUrl }) {
    const [selected, setSelected] = useState(null);
    const [showCrop, setShowCrop] = useState(false);
    const fileInputRef = React.useRef(null);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (ev) => onAdd(ev.target.result);
            reader.readAsDataURL(file);
        });
        // Reset so same file can be re-added
        e.target.value = "";
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                    {language === "zh" ? "提取的圖片" : "Extracted Images"}
                    {images && images.length > 0 && (
                        <span className="ml-2 text-xs text-gray-400 font-normal">
                            ({images.length})
                        </span>
                    )}
                </label>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-500/10 hover:bg-orange-500/20 px-3 py-1.5 rounded-lg transition-colors"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    {language === "zh" ? "新增圖片" : "Add Image"}
                </button>
                {fileUrl && (
                    <button
                        type="button"
                        onClick={() => setShowCrop(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-500/10 hover:bg-blue-500/20 px-3 py-1.5 rounded-lg transition-colors"
                    >
                        <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2m8-16h2a2 2 0 012 2v2m0 8v2a2 2 0 01-2 2h-2"
                            />
                        </svg>
                        {language === "zh" ? "從 PDF 截取" : "Capture from PDF"}
                    </button>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                />
            </div>

            {images && images.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                    {images.map((src, i) => (
                        <div key={i} className="relative group">
                            <button
                                type="button"
                                onClick={() => setSelected(src)}
                                className="w-20 h-20 rounded-xl border-2 border-gray-200 overflow-hidden hover:border-orange-400 transition-colors block"
                            >
                                <img
                                    src={src}
                                    alt={`img-${i}`}
                                    className="w-full h-full object-cover"
                                />
                            </button>
                            {/* Delete button — shown on hover */}
                            <button
                                type="button"
                                onClick={() => onDelete(i)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full items-center justify-center hidden group-hover:flex transition-colors shadow-sm"
                                title={
                                    language === "zh"
                                        ? "刪除圖片"
                                        : "Delete image"
                                }
                            >
                                <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2.5}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded-xl">
                    {language === "zh" ? "尚無圖片" : "No images yet"}
                </p>
            )}

            {selected && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                    onClick={() => setSelected(null)}
                >
                    <img
                        src={selected}
                        alt="preview"
                        className="max-w-3xl max-h-[85vh] rounded-2xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            {showCrop && (
                <PdfCropModal
                    fileUrl={fileUrl}
                    language={language}
                    onCapture={(dataUrl) => {
                        onAdd(dataUrl);
                        setShowCrop(false);
                    }}
                    onClose={() => setShowCrop(false)}
                />
            )}
        </div>
    );
}

// ── Product Selector (multi-product) ─────────────────────────────────────────
function ProductSelector({
    products,
    activeIndex,
    onSelect,
    savedProducts,
    language,
}) {
    if (products.length <= 1) return null;
    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">
                {language === "zh"
                    ? `找到 ${products.length} 個產品，請選擇要編輯的：`
                    : `${products.length} products found — select one to edit:`}
            </p>
            <div className="flex flex-wrap gap-2">
                {products.map((p, i) => {
                    const isSaved = savedProducts.has(i);
                    const isActive = i === activeIndex;
                    return (
                        <button
                            key={i}
                            onClick={() => !isSaved && onSelect(i)}
                            disabled={isSaved}
                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border flex items-center gap-1.5 ${
                                isSaved
                                    ? "bg-green-50 text-green-600 border-green-200 cursor-default line-through opacity-60"
                                    : isActive
                                      ? "bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/20"
                                      : "bg-white text-gray-700 border-gray-200 hover:border-orange-300"
                            }`}
                        >
                            {isSaved && (
                                <svg
                                    className="w-3.5 h-3.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2.5}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            )}
                            {p.model_id || `Product ${i + 1}`}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
const CompetitorProductProperties = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const language = new URLSearchParams(location.search).get("lang") || "en";

    const rawData = location.state?.data;
    const selection = location.state?.selection || {};
    const pdfFile = location.state?.file || null;
    const products = rawData?.products || [];
    const termChanges = rawData?.term_changes;
    console.log(
        "[CompetitorProductProperties] rawData:",
        rawData,
        "termChanges:",
        termChanges,
    );

    // Create object URL for the PDF file
    const [fileUrl, setFileUrl] = useState(null);
    useEffect(() => {
        if (pdfFile) {
            const url = URL.createObjectURL(pdfFile);
            setFileUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [pdfFile]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [savedProducts, setSavedProducts] = useState(new Set()); // indices saved
    const [status, setStatus] = useState("");
    const [conflict, setConflict] = useState(null); // { existing_product, formData } | null

    const [attributeTypePicker, setAttributeTypePicker] = useState(false);

    // Per-product editable state — one entry per product
    const [editStates, setEditStates] = useState(() =>
        products.map((p) => ({
            modelId: p.model_id || "",
            description: p.description || "",
            pdfUrl: p.pdf_url || "",
            rohsCompliant: p.rohs_compliant ?? false,
            reachCompliant: p.reach_compliant ?? false,
            ulComparable: p.ul_comparable ?? false,
            attributes: p.attributes || [],
            images: p.images || [],
        })),
    );

    if (!products.length) {
        return (
            <div className="flex h-screen bg-gray-50 text-black font-sans">
                <Sidebar language={language} />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-gray-500 mb-4">
                            {language === "zh"
                                ? "找不到產品資料"
                                : "No product data found"}
                        </p>
                        <button
                            onClick={() => navigate(-1)}
                            className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors"
                        >
                            {language === "zh" ? "返回" : "Go Back"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const current = editStates[activeIndex];

    const updateCurrent = (patch) => {
        setEditStates((prev) =>
            prev.map((s, i) => (i === activeIndex ? { ...s, ...patch } : s)),
        );
    };

    const handleAttrChange = (i, updated) => {
        updateCurrent({
            attributes: current.attributes.map((a, idx) =>
                idx === i ? updated : a,
            ),
        });
    };
    const handleAttrDelete = (i) => {
        updateCurrent({
            attributes: current.attributes.filter((_, idx) => idx !== i),
        });
    };
    const handleAttrMove = (i, dir) => {
        const attrs = [...current.attributes];
        const target = i + dir;
        if (target < 0 || target >= attrs.length) return;
        [attrs[i], attrs[target]] = [attrs[target], attrs[i]];
        updateCurrent({ attributes: attrs });
    };

    const handleAddAttribute = (type) => {
        const defaultData = {
            string: "",
            list: [],
            table: [], // flat array of row-objects: [{ colName: value, ... }]
        };

        const newAttribute = {
            dataType: type,
            attributeName: "",
            data: defaultData[type] ?? {},
        };
        updateCurrent({ attributes: [newAttribute, ...current.attributes] });
        setAttributeTypePicker(false);
    };

    // ── Build FormData (shared by save + overwrite) ───────────────────────────
    const buildFormData = (forceUpdate = false) => {
        const formData = new FormData();
        formData.append("model_id", current.modelId.trim());
        if (selection.categoryId != null)
            formData.append("category_id", selection.categoryId);
        if (selection.producerId != null)
            formData.append("producer_id", selection.producerId);
        formData.append("description", current.description || "");
        formData.append(
            "rohs_compliant",
            current.rohsCompliant ? "true" : "false",
        );
        formData.append(
            "reach_compliant",
            current.reachCompliant ? "true" : "false",
        );
        formData.append(
            "ul_comparable",
            current.ulComparable ? "true" : "false",
        );
        formData.append("attributes", JSON.stringify(current.attributes || []));
        // Convert base64 data URLs → Blob files so the backend uploads them to GCS.
        // If an entry is already a plain URL (starts with http), skip it.
        const imageItems = current.images || [];
        imageItems.forEach((src, idx) => {
            if (typeof src === "string" && src.startsWith("data:")) {
                const [header, b64] = src.split(",");
                const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
                const ext = mime.split("/")[1] || "jpeg";
                const binary = atob(b64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++)
                    bytes[i] = binary.charCodeAt(i);
                const blob = new Blob([bytes], { type: mime });
                formData.append("images[]", blob, `img_${idx}.${ext}`);
            }
            // Already-uploaded GCS URLs are ignored here; the existing pdf_url
            // field handles the PDF similarly. If you want to preserve existing
            // image URLs on update you would need a separate field.
        });
        if (pdfFile) {
            formData.append("pdf", pdfFile);
        } else if (current.pdfUrl) {
            formData.append("pdf_url", current.pdfUrl);
        }
        if (forceUpdate) formData.append("force_update", "true");
        return formData;
    };

    // ── Post save success ─────────────────────────────────────────────────────
    const onSaveSuccess = () => {
        setSavedProducts((prev) => new Set([...prev, activeIndex]));
        const nextUnsaved = products.findIndex(
            (_, i) => i !== activeIndex && !savedProducts.has(i),
        );
        if (nextUnsaved !== -1) {
            setActiveIndex(nextUnsaved);
            setStatus(
                language === "zh"
                    ? `「${current.modelId}」已儲存！請繼續下一個產品。`
                    : `"${current.modelId}" saved! Continue with the next product.`,
            );
        } else if (savedProducts.size + 1 >= products.length) {
            navigate(`/product-competitor?lang=${language}`);
        } else {
            setStatus(
                language === "zh"
                    ? `「${current.modelId}」已儲存！`
                    : `"${current.modelId}" saved!`,
            );
        }
    };

    const handleSave = async () => {
        if (!currentUser) return;
        if (!current.modelId.trim()) {
            setStatus(
                language === "zh" ? "型號不能為空" : "Model ID is required",
            );
            return;
        }
        setSaving(true);
        setStatus("");
        try {
            const token = await currentUser.getIdToken();
            await axios.post(
                `${getApiUrl()}/api/v2/competitor-products/`,
                buildFormData(false),
                { headers: { Authorization: `Bearer ${token}` } },
            );
            onSaveSuccess();
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.conflict) {
                // Show conflict modal — keep the pending formData for potential overwrite
                setConflict(err.response.data);
            } else {
                const msg = err.response?.data?.error || err.message;
                setStatus(
                    language === "zh"
                        ? `儲存失敗: ${msg}`
                        : `Save failed: ${msg}`,
                );
            }
        } finally {
            setSaving(false);
        }
    };

    // ── Conflict modal actions ────────────────────────────────────────────────
    const handleConflictCancel = () => {
        // Close modal, user stays on the form to keep editing
        setConflict(null);
    };

    const handleConflictSkip = () => {
        // Treat this product as "done" without saving, move on
        setConflict(null);
        setSavedProducts((prev) => new Set([...prev, activeIndex]));
        const nextUnsaved = products.findIndex(
            (_, i) => i !== activeIndex && !savedProducts.has(i),
        );
        if (nextUnsaved !== -1) {
            setActiveIndex(nextUnsaved);
            setStatus(
                language === "zh"
                    ? `「${current.modelId}」已略過`
                    : `"${current.modelId}" skipped`,
            );
        } else if (savedProducts.size + 1 >= products.length) {
            navigate(`/product-competitor?lang=${language}`);
        } else {
            setStatus(language === "zh" ? "已略過" : "Skipped");
        }
    };

    const handleConflictOverwrite = async () => {
        if (!currentUser) return;
        setConflict(null);
        setSaving(true);
        setStatus("");
        try {
            const token = await currentUser.getIdToken();
            await axios.post(
                `${getApiUrl()}/api/v2/competitor-products/`,
                buildFormData(true),
                { headers: { Authorization: `Bearer ${token}` } },
            );
            onSaveSuccess();
        } catch (err) {
            const msg = err.response?.data?.error || err.message;
            setStatus(
                language === "zh"
                    ? `覆蓋失敗: ${msg}`
                    : `Overwrite failed: ${msg}`,
            );
        } finally {
            setSaving(false);
        }
    };

    const allSaved = savedProducts.size >= products.length;

    return (
        <div className="flex h-screen bg-dashboard-bg text-black font-sans">
            <Sidebar language={language} />
            <LanguageSwitcher language={language} setLanguage={() => {}} />

            <div className="w-5/6 flex-1 flex flex-col gap-4 p-8 md:p-16 pt-24 md:pt-24 overflow-y-auto">
                <ProgressSteps currentStep={2} language={language} />
                {/* Header */}
                <div className="flex flex-row justify-center items-center mb-2">
                    <button
                        onClick={() =>
                            navigate(
                                {
                                    pathname: "/product-competitor",
                                    search: `?lang=${language}`,
                                },
                                { state: { activeTab: "database" } },
                            )
                        }
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 self-start"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        {language === "zh" ? "返回" : "Back"}
                    </button>
                    <div className="mt-4 mb-2 flex-1 flex flex-col items-center text-center">
                        <h1 className="text-3xl font-bold text-white mb-1">
                            {language === "zh"
                                ? "競品資料"
                                : "Competitor Products"}
                        </h1>
                        <p className="text-slate-100 text-sm">
                            {language === "zh"
                                ? "確認 AI 擷取的資料後儲存競品"
                                : "Confirm AI-extracted data before saving competitor products"}
                        </p>
                    </div>
                </div>
                {/* Multi-product selector */}
                <ProductSelector
                    products={products}
                    activeIndex={activeIndex}
                    onSelect={setActiveIndex}
                    savedProducts={savedProducts}
                    language={language}
                />
                {/* Two-column layout: PDF viewer (left) + form (right) */}
                <div className="flex flex-col lg:flex-row gap-6">
                    {/* PDF Viewer */}
                    {fileUrl && (
                        <div className="lg:w-2/5 mb-8 lg:mb-0">
                            <div className="bg-white rounded-2xl border border-gray-200 p-2 sticky top-4">
                                <div
                                    className="bg-gray-100 rounded-lg overflow-hidden"
                                    style={{ height: "700px", width: "100%" }}
                                >
                                    <iframe
                                        src={fileUrl}
                                        title="PDF Preview"
                                        className="w-full h-full border-0"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Form */}
                    <div
                        className={`bg-white rounded-2xl border border-gray-200 p-6 md:p-8 space-y-6 ${fileUrl ? "lg:w-3/5" : "w-full"}`}
                    >
                        {/* Already saved badge */}
                        {savedProducts.has(activeIndex) && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-medium">
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                                {language === "zh"
                                    ? "此產品已儲存"
                                    : "This product has been saved"}
                            </div>
                        )}

                        {/* Term changes (read-only) */}
                        <TermChangesPanel
                            termChanges={termChanges}
                            language={language}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Model ID */}
                            <div className="">
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    {language === "zh" ? "型號" : "Model ID"}
                                    <span className="text-red-400 ml-0.5">
                                        *
                                    </span>
                                </label>
                                <input
                                    value={current.modelId}
                                    onChange={(e) =>
                                        updateCurrent({
                                            modelId: e.target.value,
                                        })
                                    }
                                    className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                                    placeholder="e.g. TGP3500PT"
                                />
                            </div>
                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {language === "zh" ? "生產商" : "Producer"}
                                </label>
                                <input
                                    type="text"
                                    value={selection.producerName}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    disabled
                                />
                            </div>
                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {language === "zh"
                                        ? "產品類別"
                                        : "Product Category"}
                                </label>
                                <input
                                    type="text"
                                    value={selection.categoryName}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    disabled
                                />
                            </div>
                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {language === "zh"
                                        ? "產品子類別"
                                        : "Product Sub-category"}
                                </label>
                                <input
                                    type="text"
                                    value={selection.subCategoryName || ""}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    placeholder={
                                        language === "zh"
                                            ? "無選產品子類別"
                                            : "No Product Sub-category Selected"
                                    }
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                {language === "zh" ? "描述" : "Description"}
                            </label>
                            <textarea
                                value={current.description}
                                onChange={(e) =>
                                    updateCurrent({
                                        description: e.target.value,
                                    })
                                }
                                rows={4}
                                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all resize-y"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row">
                                <label className="text-sm font-medium text-gray-700 self-end">
                                    {language === "zh"
                                        ? "產品合規性"
                                        : "Product Compliance"}
                                </label>
                            </div>
                            <div className="max-h-48 overflow-y-auto px-5">
                                <div className="flex flex-col gap-1">
                                    <div
                                        className="flex justify-between items-center p-2 bg-white border border-gray-300 hover:bg-gray-300 rounded-lg cursor-pointer"
                                        onClick={() =>
                                            updateCurrent({
                                                reachCompliance:
                                                    !current.reachCompliance,
                                            })
                                        }
                                    >
                                        <label className="text-sm font-medium text-gray-700 cursor-pointer">
                                            REACH Compliant
                                        </label>
                                        <input
                                            type="checkbox"
                                            checked={current.reachCompliance}
                                            onChange={() => {}}
                                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer pointer-events-none"
                                        />
                                    </div>
                                    <div
                                        className="flex justify-between items-center p-2 bg-white border border-gray-300 hover:bg-gray-300 rounded-lg cursor-pointer"
                                        onClick={() =>
                                            updateCurrent({
                                                rohsCompliance:
                                                    !current.rohsCompliance,
                                            })
                                        }
                                    >
                                        <label className="text-sm font-medium text-gray-700 cursor-pointer">
                                            RoHS Compliant
                                        </label>
                                        <input
                                            type="checkbox"
                                            checked={current.rohsCompliance}
                                            onChange={() => {}}
                                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer pointer-events-none"
                                        />
                                    </div>
                                    <div
                                        className="flex justify-between items-center p-2 bg-white border border-gray-300 hover:bg-gray-300 rounded-lg cursor-pointer"
                                        onClick={() =>
                                            updateCurrent({
                                                ulComparable:
                                                    !current.ulComparable,
                                            })
                                        }
                                    >
                                        <label className="text-sm font-medium text-gray-700 cursor-pointer">
                                            UL Comparable
                                        </label>
                                        <input
                                            type="checkbox"
                                            checked={current.ulComparable}
                                            onChange={() => {}}
                                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer pointer-events-none"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Images */}
                        <ImageGallery
                            images={current.images}
                            language={language}
                            fileUrl={fileUrl}
                            onDelete={(i) =>
                                updateCurrent({
                                    images: current.images.filter(
                                        (_, idx) => idx !== i,
                                    ),
                                })
                            }
                            onAdd={(dataUrl) =>
                                updateCurrent({
                                    images: [...current.images, dataUrl],
                                })
                            }
                        />

                        {/* Attributes */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-semibold text-gray-700">
                                    {language === "zh" ? "屬性" : "Attributes"}
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        className="bg-green-500 outline outline-1 outline-green-200 px-4 py-1 rounded-lg text-white hover:bg-green-600 transition-colors text-xs md:text-sm"
                                        onClick={() =>
                                            setAttributeTypePicker(
                                                !attributeTypePicker,
                                            )
                                        }
                                    >
                                        +{" "}
                                        {language === "zh"
                                            ? "添加產品屬性"
                                            : "Add Product Attribute"}
                                    </button>
                                    {attributeTypePicker && (
                                        <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
                                            <button
                                                type="button"
                                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors"
                                                onClick={() =>
                                                    handleAddAttribute("string")
                                                }
                                            >
                                                {language === "zh"
                                                    ? "字串類型"
                                                    : "String Type"}
                                            </button>
                                            <button
                                                type="button"
                                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-purple-50 transition-colors"
                                                onClick={() =>
                                                    handleAddAttribute("list")
                                                }
                                            >
                                                {language === "zh"
                                                    ? "列表類型"
                                                    : "List Type"}
                                            </button>
                                            <button
                                                type="button"
                                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-purple-50 transition-colors"
                                                onClick={() =>
                                                    handleAddAttribute("table")
                                                }
                                            >
                                                {language === "zh"
                                                    ? "表格類型"
                                                    : "Table Type"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-3">
                                {current.attributes.map((attr, i) => {
                                    const total = current.attributes.length;
                                    const moveButtons = (
                                        <div className="flex flex-col gap-0.5 ml-1 shrink-0">
                                            <button
                                                type="button"
                                                disabled={i === 0}
                                                onClick={() => handleAttrMove(i, -1)}
                                                className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                title={language === "zh" ? "上移" : "Move up"}
                                            >
                                                ▲
                                            </button>
                                            <button
                                                type="button"
                                                disabled={i === total - 1}
                                                onClick={() => handleAttrMove(i, 1)}
                                                className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                title={language === "zh" ? "下移" : "Move down"}
                                            >
                                                ▼
                                            </button>
                                        </div>
                                    );
                                    if (attr.dataType === "list")
                                        return (
                                            <div key={i} className="flex items-start">
                                                <div className="flex-1 min-w-0">
                                                    <ListType
                                                        attribute={attr}
                                                        onChange={(u) =>
                                                            handleAttrChange(i, u)
                                                        }
                                                        onDelete={() =>
                                                            handleAttrDelete(i)
                                                        }
                                                        language={language}
                                                    />
                                                </div>
                                                {moveButtons}
                                            </div>
                                        );
                                    if (attr.dataType === "string")
                                        return (
                                            <div key={i} className="flex items-start">
                                                <div className="flex-1 min-w-0">
                                                    <StringType
                                                        attribute={attr}
                                                        onChange={(u) =>
                                                            handleAttrChange(i, u)
                                                        }
                                                        onDelete={() =>
                                                            handleAttrDelete(i)
                                                        }
                                                        language={language}
                                                    />
                                                </div>
                                                {moveButtons}
                                            </div>
                                        );
                                    if (attr.dataType === "table")
                                        return (
                                            <div key={i} className="flex items-start">
                                                <div className="flex-1 min-w-0">
                                                    <TableType
                                                        attribute={attr}
                                                        onChange={(u) =>
                                                            handleAttrChange(i, u)
                                                        }
                                                        onDelete={() =>
                                                            handleAttrDelete(i)
                                                        }
                                                        language={language}
                                                    />
                                                </div>
                                                {moveButtons}
                                            </div>
                                        );
                                    return null;
                                })}
                                {current.attributes.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded-xl">
                                        {language === "zh"
                                            ? "尚無屬性"
                                            : "No attributes yet"}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Status */}
                        {status && (
                            <div
                                className={`p-4 rounded-xl text-sm border ${status.includes("saved") || (status.includes("儲存") && !status.includes("失敗")) ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}
                            >
                                {status}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <button
                                onClick={() =>
                                    navigate(
                                        {
                                            pathname: "/product-competitor",
                                            search: `?lang=${language}`,
                                        },
                                        { state: { activeTab: "database" } },
                                    )
                                }
                                className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                            >
                                {language === "zh" ? "返回" : "Back"}
                            </button>
                            <div className="flex items-center gap-3">
                                {allSaved && (
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/product-competitor?lang=${language}`,
                                            )
                                        }
                                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                                    >
                                        {language === "zh" ? "完成" : "Done"}
                                    </button>
                                )}
                                <button
                                    onClick={handleSave}
                                    disabled={
                                        saving || savedProducts.has(activeIndex)
                                    }
                                    className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${
                                        saving || savedProducts.has(activeIndex)
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"
                                    }`}
                                >
                                    {saving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            {language === "zh"
                                                ? "儲存中…"
                                                : "Saving…"}
                                        </>
                                    ) : (
                                        <>
                                            {language === "zh"
                                                ? "儲存競品"
                                                : "Save Product"}
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>{" "}
                {/* end flex flex-col lg:flex-row */}
            </div>

            {/* Conflict resolution modal */}
            {conflict && (
                <ConflictModal
                    conflict={conflict}
                    incoming={{
                        ...current,
                        model_id: current.modelId,
                        producerName: selection.producerName,
                        categoryName: selection.categoryName,
                        pdf_url: current.pdfUrl,
                        rohs_compliant: current.rohsCompliant,
                        reach_compliant: current.reachCompliant,
                        ul_comparable: current.ulComparable,
                    }}
                    language={language}
                    onCancel={handleConflictCancel}
                    onSkip={handleConflictSkip}
                    onOverwrite={handleConflictOverwrite}
                />
            )}
        </div>
    );
};

export default CompetitorProductProperties;
